import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { CheckboxField } from '@/components/form/checkbox/Checkbox';
import { TsForm } from '@/components/form/Form';
import { GridSelectField } from '@/components/form/GridSelectInput';
import { SelectInputField } from '@/components/form/SelectInput';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { GridDropdownType, GridSelectOption } from '@/components/grid/GridSelect';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { colorArray, DEFAULT_WHITE_COLOR_ARRAY_OPTION } from '@/helpers/dropdownOptionHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useFlag } from '@/services/backstage/Backstage';
import { LeaveType } from '@/services/leaveManagement/leave.types';
import { useUpdateLeaveType } from '@/services/leaveManagement/LeaveTypesProvider';
import { TextColors } from '@/services/tasks/task.types';
import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FiCalendar } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import strings from '../../admin.translations';
import { textColorOptions } from '../../tasks/edit/AdminEditTaskDetailsCard';
import { AdminLeaveTypeDeleteDialog } from '../AdminLeaveTypeDeleteDialog';

interface LeaveTypeEditCardProps {
  leaveType: LeaveType;
}

interface LeaveTypeFormValues {
  name: string;
  displayName: string;
  code: string;
  colorId: string;
  textColor: TextColors;
  managerOnly: boolean;
  active: boolean;
}

const AdminEditLeaveTypeDetailsCard: FC<LeaveTypeEditCardProps> = ({ leaveType }) => {
  const isDebug = useFlag('debug');

  const colourLookup = useMemo(
    () => colorArray.find(option => option.value === leaveType.properties.color),
    [leaveType.properties.color]
  );

  const methods = useForm<LeaveTypeFormValues>({
    defaultValues: {
      name: leaveType.name,
      displayName: leaveType.properties.displayName,
      code: leaveType.properties.systemCode,
      colorId: colourLookup?.id ?? DEFAULT_WHITE_COLOR_ARRAY_OPTION.id,
      textColor: leaveType.properties.textColor,
      managerOnly: leaveType.properties.managerOnly,
      active: leaveType.active
    }
  });

  useEffect(() => {
    methods.reset();
  }, [leaveType, colourLookup, methods]);

  const watchedDisplayName = useWatch({
    control: methods.control,
    name: 'displayName'
  });

  const watchedTextColor = useWatch({
    control: methods.control,
    name: 'textColor'
  });

  const watchedColorId = useWatch({
    control: methods.control,
    name: 'colorId'
  });

  const previewBackground = useMemo(() => {
    return colorArray.find(option => option.id === watchedColorId)?.value;
  }, [watchedColorId]);

  const stringPrefix = strings.leaveTypeSection.edit;

  const { fetching, update } = useUpdateLeaveType();

  const handleSubmit = async (values: LeaveTypeFormValues) => {
    const selectedColour = colorArray.find(option => option.id === values.colorId)?.value;

    const updatedProperties = {
      ...leaveType.properties,
      displayName: values.displayName,
      color: selectedColour,
      systemCode: values.code,
      textColor: values.textColor,
      managerOnly: values.managerOnly
    };

    const result = await update({
      ...leaveType,
      name: values.name,
      properties: updatedProperties
    });

    await handleMutationResult(result, {
      successMessage: stringPrefix.successMessage(values.name),
      errorMessage: stringPrefix.errorMessage(leaveType.name),
      customSuccessCheck: data => !!data?.updateLeaveType,
      onSuccess: () => {
        handleClose();
      }
    });
  };

  const navigate = useNavigate();
  const handleClose = () => {
    methods.reset();
    navigate('/admin/leave-types');
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  return (
    <TSTitleCard
      title={stringPrefix.title(leaveType.properties.displayName || leaveType.name)}
      icon={FiCalendar}
      className="w-full"
    >
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={handleSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField label={stringPrefix.name} name="name" id="name" required />

            <TextField
              label={`${stringPrefix.displayName} - ${stringPrefix.displayNameHelp}`}
              name="displayName"
              id="displayName"
              required
            />

            <TextField label={stringPrefix.code} name="code" id="code" required />

            <div className="flex flex-row gap-6 items-start">
              <GridSelectField
                id="colorId"
                name="colorId"
                label={stringPrefix.backgroundColor}
                type={GridDropdownType.COLOR}
                dropdownOptions={colorArray}
                itemsPerRow={6}
                dropdownId="leaveTypeColour"
              >
                {colorArray.map((color, index) => (
                  <GridSelectOption
                    key={index}
                    className="p-1.5 text-2xl w-8 h-8 rounded-full"
                    option={color}
                    type={GridDropdownType.COLOR}
                  />
                ))}
              </GridSelectField>

              <SelectInputField
                label={stringPrefix.textColor}
                options={textColorOptions}
                name="textColor"
                id="textColor"
              />

              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">{stringPrefix.preview}</span>

                <div
                  className="h-10 min-w-[120px] px-3 flex items-center justify-center rounded-md"
                  style={{
                    background: previewBackground,
                    color: watchedTextColor
                  }}
                >
                  {watchedDisplayName}
                </div>
              </div>
            </div>

            <CheckboxField
              id="managerOnly"
              name="managerOnly"
              label={stringPrefix.managerOnly}
              description={stringPrefix.managerOnlyDescription}
            />

            <div className="flex items-center justify-between gap-3">
              <div>
                {isDebug && (
                  <Button danger onClick={() => setDeleteModalOpen(true)} disabled={fetching} type="button">
                    {strings.common.delete}
                  </Button>
                )}
              </div>

              <SubmitCancelButtons onComplete={handleClose} loading={fetching} submitText={strings.common.save} />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>

      <AdminLeaveTypeDeleteDialog
        leaveType={leaveType}
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      />
    </TSTitleCard>
  );
};

export default AdminEditLeaveTypeDetailsCard;
